/**
 * Configuración de entorno del frontend.
 *
 * `VITE_API_URL` se define en `frontend/.env` (ver `.env.example`). Si no está, se usa el
 * puerto por defecto del backend (`backend/.env` → `PORT=3000`).
 *
 * `import.meta.env` se lee tolerando que no exista: dentro del bundle de Vite siempre está
 * (Vite lo reemplaza por un objeto literal), y fuera de Vite (ej. `npm run smoke`, que corre
 * con tsx en Node) cae al valor por defecto en vez de romper.
 */

const DEFAULT_API_URL = 'http://localhost:3000';

const viteEnv: ImportMetaEnv = import.meta.env ?? {};

/**
 * Las mismas variables leídas de `process.env` cuando NO hay Vite (los scripts con tsx, ej.
 * `npm run smoke:settings`): así una configuración opcional se puede probar en sus dos ramas sin
 * tocar archivos. Se lee por `globalThis` (y no por `process`) para que el bundle del navegador no
 * necesite los tipos de Node: si no existe, queda un objeto vacío.
 */
const nodeEnv: Record<string, string | undefined> =
  (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env ?? {};

/** Quita la barra final para poder concatenar rutas sin duplicarla. */
function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function readEnv(key: keyof ImportMetaEnv): string {
  return (viteEnv[key] ?? nodeEnv[key] ?? '').trim();
}

export const env = {
  apiUrl: normalizeBaseUrl(readEnv('VITE_API_URL') || DEFAULT_API_URL),
  /** App ID de Meta para el JS SDK de Embedded Signup (vacío = no configurado). */
  metaAppId: readEnv('VITE_META_APP_ID'),
  /** Configuración de Facebook Login for Business del flujo de Embedded Signup. */
  metaEmbeddedSignupConfigId: readEnv('VITE_META_ES_CONFIG_ID'),
  isDev: viteEnv.DEV ?? false,
  isProd: viteEnv.PROD ?? false,
} as const;

/**
 * Convierte una ruta del backend en URL absoluta.
 * Se usa para archivos estáticos: las fotos de las guías de envío que sirve
 * `GET /uploads/...` (módulo 7) o el logo del tenant.
 */
export function publicUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${env.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

